import axios from 'axios';
import { NotificationManager } from 'react-notifications';
import i18next from 'i18next';
import { getTenantPrefix } from './ProxyPathService';
import { getConfigCache } from './configCacheService';
import { getAccessToken } from './authTokenManager';
// TODO(old-api-support): drop this import and the observeWireFormat() call below once every
// deployed API sends real UTC-Z dates.
import { observeWireFormat } from './ApiWireFormat';
import API from 'constants/Api';

let cachedBaseUrl = null;

const getApiBaseUrl = async () => {
  if (!cachedBaseUrl) {
    const { BASE } = getConfigCache();
    const tenantPrefix = getTenantPrefix();
    const apiBasePath = BASE.replace('/api/', `/api/${tenantPrefix}`);
    cachedBaseUrl = `${process.env.NODE_ENV === 'production' ? window.location.origin : ''}${apiBasePath}`;
  }

  return cachedBaseUrl;
};
  
/**
 * Resets the cached API base URL with tenantId prefix.
 *
 * Calling this ensures that the next request will recompute the base URL using the latest tenant configuration. This is useful after changes like tenant switch, login, or logout.
 * 
 * Note: Previously, `userSite` in `localStorage` was used to force a refresh of the base URL. That approach has been deprecated.
 */
export const resetApiBaseUrl = () => {
  cachedBaseUrl = null;
};

export const axiosA = axios.create({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

const requestInterceptor = async (config) => {
  config.baseURL = await getApiBaseUrl();
  const token = getAccessToken();
  if (token) config.headers[API.TOKEN_HEADER_KEY] = `${API.BEARER} ${token}`;
  return config;
};

axiosA.interceptors.request.use(requestInterceptor);
axiosA.interceptors.response.use(
  (response) => {
    observeWireFormat(response.data);
    return response;
  },
  function (error) {
    const eStatus = error.response.status;
    const originalRequest = error.config;
    if (eStatus === 400 || eStatus === 401 || eStatus === 403 || eStatus === 451) {
      if (eStatus === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const token = getAccessToken();
        if (token) {
          return axiosA(originalRequest);
        } else if (!token) {
          //new auth token cannot be taken
          localStorage.clear();
          window.location.reload();
          return Promise.reject('unathorized');
        }
      }

      if (eStatus === 451) {
        if (window.location.pathname !== '/consent') {
           //localStorage.setItem('redirectToAfterConsent', window.location.pathname);
          console.log(`error response headers ${error.response.headers['location']}`)
          window.location.pathname = `${error.response.headers['location']}`;
        }
        return Promise.reject('consent.redirectTo');
      }

      let e = error.response.data;
      let msg = '';

      if (e.details) {
        e.details.forEach((element) => {
          // errorKey is server-combined from ad-hoc validation messages, so a translation isn't guaranteed.
          msg += `${i18next.t(['error.' + element.errorKey, 'error.Generic'], {
            errorKey: element.errorKey,
            defaultValue: 'Something went wrong (code: {{errorKey}})',
          })} \r\n`;
        });

        NotificationManager.error(msg);
      }

      return Promise.reject(msg);
    }

    if (error.response.status === 500) {
      NotificationManager.error(`${i18next.t(`error.id`)}: ${error.response.data.errorId}`, i18next.t(`error.500`), 0);
      return Promise.reject(error.response.data.errorId);
    }

    if (error.response.status === 502 || error.response.status === 503 || error.response.status === 504) {
      console.log('error', error.response.status);
      NotificationManager.error(`${i18next.t(`error.SystemAwayPleaseTryLater`)}`, i18next.t(`error.${error.response.status}`), 0);
      return Promise.reject(error.response.data.errorId);
    }

    return Promise.reject(error.response);
  }
);